import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  APIgetPageCollectionDetail,
  APIgetPageCollectionFiles,
} from '../../../../api/Pages';
import { APIgetFilesAsync } from '../../../../api/Files';

const FILES_PER_PAGE = 36;
const MAX_LIMIT = 36;
const liveFeedCache = new Map();

export const useLiveCollectionFeed = (
  collection,
  pageId,
  limit = 0,
  isEditing = false,
  allowedCollectionUuids = [],
  view = 'grid',
) => {
  const [liveCollection, setLiveCollection] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const pageRef = useRef(1);
  const normalizedAllowedUuids = useMemo(
    () => Array.from(new Set(
      (Array.isArray(allowedCollectionUuids) ? allowedCollectionUuids : []).filter(Boolean),
    )).sort(),
    [allowedCollectionUuids],
  );
  const allowedSet = useMemo(() => new Set(normalizedAllowedUuids), [normalizedAllowedUuids]);
  const allowedKey = normalizedAllowedUuids.join(',');
  const useAPIFiles = isEditing && !allowedSet.has(collection?.uuid);
  const cacheKey = `${collection?.uuid || ''}|${pageId || ''}|${limit || 0}|${isEditing ? 1 : 0}|${allowedKey}|${view}`;

  const fetchPage = useCallback((pg, append = false) => {
    if (!collection?.uuid) return;
    if (pg === 1) setLoading(true);
    else setLoadingMore(true);

    const perPage = limit > 0 ? Math.min(limit, MAX_LIMIT) : FILES_PER_PAGE;

    const request = useAPIFiles
      ? APIgetFilesAsync({
        forPage: true,
        collectionUuid: collection.uuid,
        page: pg,
        per_page: perPage,
        view,
      })
      : APIgetPageCollectionFiles(pageId, collection.uuid, { page: pg, per_page: perPage, view });

    request
      .then((res) => {
        let items = [];
        if (Array.isArray(res.data?.data)) {
          items = res.data.data;
        } else if (Array.isArray(res.data?.data?.data)) {
          items = res.data.data.data;
        }
        let nextFiles = items;
        if (append) {
          setFiles((prev) => {
            nextFiles = [...prev, ...items];
            return nextFiles;
          });
        } else {
          setFiles(items);
        }

        const apiHasMore = !useAPIFiles
          ? (res.data?.data?.meta?.has_more ?? res.data?.meta?.has_more ?? false)
          : false;

        const underLimit = limit <= 0 || nextFiles.length < limit;
        const nextHasMore = apiHasMore && underLimit;

        setHasMore(nextHasMore);
        liveFeedCache.set(cacheKey, {
          files: nextFiles,
          hasMore: nextHasMore,
          page: pg,
        });
      })
      .catch(() => {
        setFiles([]);
        setHasMore(false);
        liveFeedCache.delete(cacheKey);
      })
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  }, [cacheKey, collection?.uuid, pageId, limit, useAPIFiles, view]);

  useEffect(() => {
    if (!collection?.uuid) {
      setLiveCollection(null);
      setFiles([]);
      setLoading(false);
      setHasMore(false);
      return;
    }

    const cachedFeed = liveFeedCache.get(cacheKey);
    if (cachedFeed) {
      setFiles(cachedFeed.files || []);
      setHasMore(!!cachedFeed.hasMore);
      pageRef.current = cachedFeed.page || 1;
      setLoading(false);
      return;
    }

    pageRef.current = 1;
    fetchPage(1, false);
  }, [cacheKey, collection?.uuid, fetchPage]);

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    pageRef.current += 1;
    fetchPage(pageRef.current, true);
  }, [loadingMore, hasMore, fetchPage]);

  return {
    liveCollection,
    files,
    loading,
    loadingMore,
    hasMore,
    loadMore,
  };
};

export const usePageCollectionDetail = (pageId, collectionUuid) => {
  const [childCollections, setChildCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!collectionUuid) {
      setChildCollections([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    APIgetPageCollectionDetail(pageId, collectionUuid)
      .then((res) => {
        setChildCollections(res.data?.child_collections ?? []);
      })
      .catch(() => {
        setChildCollections([]);
      })
      .finally(() => setLoading(false));
  }, [pageId, collectionUuid]);

  return { childCollections, loading };
};
